import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import JSZip from "jszip";
import { getBedrockCostChartData } from "../../../../service/apiService";
import { Download, RotateCw } from "lucide-react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line, Bar } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);


interface CostChartData {
  success: boolean;
  data: {
    labels: string[];
    values: number[];
    label: string;
    metric_type: string;
    currency: string;
  };
  statistics: {
    total: number;
    average: number;
    maximum: number;
    minimum: number;
    period: string;
  };
  chart_config: {
    chart_type: string;
    color: string;
    background_color: string;
  };
}

interface ChartProps {
  title: string;
  chartData: CostChartData | null;
  isLoading: boolean;
  error: string | null;
  chartType: "line" | "bar";
  formatLabel: (raw: string) => string;
}

export interface ChartComponentHandle {
  getPNGBlob: () => Promise<{ name: string; blob: Blob } | null>;
}


const slugifyTitle = (title: string) =>
  title
    .replace(/[^a-z0-9]+/gi, "-")
    .toLowerCase()
    .replace(/^-|-$/g, "");

const renderChartToCanvas = (
  sourceCanvas: HTMLCanvasElement
): HTMLCanvasElement | null => {
  const tempCanvas = document.createElement("canvas");
  tempCanvas.width = sourceCanvas.width;
  tempCanvas.height = sourceCanvas.height;
  const ctx = tempCanvas.getContext("2d");
  if (!ctx) return null;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
  ctx.drawImage(sourceCanvas, 0, 0);
  return tempCanvas;
};

const formatMoney = (amount: number, currency = "USD") => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }).format(amount || 0);
  } catch {
    return `${(amount || 0).toFixed(4)} ${currency}`;
  }
};

const formatDayLabel = (raw: string) => {
  const [year, month, day] = raw.split("-").map(Number);
  if (!year || !month || !day) return raw;
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.toLocaleDateString("default", {
    month: "short",
    day: "numeric",timeZone: "UTC",
  });
};

const formatMonthLabel = (raw: string) => {
  const [year, month] = raw.split("-").map(Number);
  if (!year || !month) return raw;
  const date = new Date(Date.UTC(year, month - 1, 1));
  return date.toLocaleDateString("default", {
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};


const ChartComponent = forwardRef<ChartComponentHandle, ChartProps>(
  ({ title, chartData, isLoading, error, chartType, formatLabel }, ref) => {
    const chartRef = useRef<any>(null);
    const [isDownloading, setIsDownloading] = useState(false);

    useImperativeHandle(
      ref,
      () => ({
        getPNGBlob: () =>
          new Promise((resolve) => {
            const chart = chartRef.current;
            if (!chart) {
              resolve(null);
              return;
            }
            const tempCanvas = renderChartToCanvas(
              chart.canvas as HTMLCanvasElement
            );
            if (!tempCanvas) {
              resolve(null);
              return;
            }
            tempCanvas.toBlob(
              (blob) => {
                if (!blob) {
                  resolve(null);
                  return;
                }
                resolve({ name: `${slugifyTitle(title)}.png`, blob });
              },
              "image/png",
              1
            );
          }),
      }),
      [title]
    );

    const handleDownloadPNG = () => {
      const chart = chartRef.current;
      if (!chart) return;
      setIsDownloading(true);
      try {
        const tempCanvas = renderChartToCanvas(
          chart.canvas as HTMLCanvasElement
        );
        if (!tempCanvas) return;
        const url = tempCanvas.toDataURL("image/png", 1);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${slugifyTitle(title)}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (err) {
        console.error("Error downloading chart as PNG:", err);
      } finally {
        setIsDownloading(false);
      }
    };

    //── Loading state ──────────────────────────────────────────────────────
    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-64">
          <RotateCw className="animate-spin h-5 w-5 text-violet-500 mr-2" />
          <span className="text-violet-500">Loading chart data...</span>
        </div>
      );
    }

    // ── Error state ────────────────────────────────────────────────────────
    if (error) {
      return (
        <div className="flex items-center justify-center h-64 bg-red-50 rounded-lg p-4">
          <p className="text-red-500">{error}</p>
        </div>
      );
    }

    // ── Empty state ────────────────────────────────────────────────────────
    if (!chartData) {
      return (
        <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg p-4">
          <p className="text-gray-500">No chart data available</p>
        </div>
      );
    }

    const currency = chartData.data.currency || "USD";

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: "top" as const },
        title: {
          display: true,
          text: title,
          font: { size: 14, weight: "bold" as const },
        },
        tooltip: {
          callbacks: {
            label: (context: any) =>
              `${context.dataset.label}: ${formatMoney(
                context.parsed.y,
                currency
              )}`,
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            callback: (value: any) =>
              typeof value === "number"
                ? formatMoney(value, currency)
                : value,
          },
        },
      },
    };

    const data = {
      labels: chartData.data.labels.map(formatLabel),
      datasets: [
        {
          label: chartData.data.label,
          data: chartData.data.values,
          borderColor: chartData.chart_config.color || "#7c3aed",
          backgroundColor:
            chartData.chart_config.background_color ||
            "rgba(124, 58, 237, 0.6)",
          tension: 0.3,
          fill: chartType === "line",
          borderWidth: chartType === "bar" ? 1 : 2,
        },
      ],
    };

    return (
      <div className="bg-white rounded-lg shadow p-4 relative">
        {/* ✅ Download PNG button — scoped inside chart card */}
        <button
          type="button"
          onClick={handleDownloadPNG}
          disabled={isDownloading}
          title="Download PNG"
          aria-label="Download PNG"
          className="absolute top-2 right-2 z-10 inline-flex justify-center
            items-center p-1.5 border border-transparent shadow-sm rounded-md
            text-white bg-violet-600 hover:bg-violet-700 focus:outline-none
            focus:ring-2 focus:ring-offset-2 focus:ring-violet-500
            disabled:bg-violet-400 disabled:cursor-not-allowed"
        >
          {isDownloading ? (
            <RotateCw className="animate-spin h-3.5 w-3.5" />
          ) : (
            <Download className="h-3.5 w-3.5" />
          )}
        </button>

        {/* Chart */}
        <div className="chart-wrapper h-64">
          {chartType === "line" ? (
            <Line ref={chartRef} options={options} data={data} />
          ) : (
            <Bar ref={chartRef} options={options} data={data} />
          )}
        </div>

        {/* Stats footer */}
        <div className="flex justify-around mt-4 pt-4 border-t border-gray-100">
          <div className="text-center">
            <span className="block text-sm text-violet-600 font-medium">
              Total
            </span>
            <span className="text-lg font-bold text-violet-800">
              {formatMoney(chartData.statistics.total, currency)}
            </span>
          </div>
          <div className="text-center">
            <span className="block text-sm text-violet-600 font-medium">
              Average
            </span>
            <span className="text-lg font-bold text-violet-800">
              {formatMoney(chartData.statistics.average, currency)}
            </span>
          </div>
        </div>
      </div>
    );
  }
);

ChartComponent.displayName = "ChartComponent";


export interface BedrockCostChartsHandle {
  downloadAllAsZip: (label?: string) => Promise<void>;
}


const BedrockCostCharts = forwardRef<BedrockCostChartsHandle>((_props, ref) => {
  const [dailyData, setDailyData] = useState<CostChartData | null>(null);
  const [monthlyData, setMonthlyData] = useState<CostChartData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const dailyChartRef = useRef<ChartComponentHandle>(null);
  const monthlyChartRef = useRef<ChartComponentHandle>(null);

  // Expose ZIP download to parent (BedrockCostMonthlyTable)
  useImperativeHandle(ref, () => ({
    downloadAllAsZip: async (label?: string) => {
      const entries = await Promise.all([
        dailyChartRef.current?.getPNGBlob() ?? null,
        monthlyChartRef.current?.getPNGBlob() ?? null,
      ]);
      const zip = new JSZip();
      let added = 0;
      for (const entry of entries) {
        if (entry) {
          zip.file(entry.name, entry.blob);
          added += 1;
        }
      }
      if (added === 0) return;
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const zipName = `${label ?? "bedrock-cost"}-charts.zip`;
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = zipName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
  }));

  useEffect(() => {
    const fetchChartData = async () => {
      setLoading(true);
      try {
        const result = await getBedrockCostChartData();
        setDailyData(result.data.daily_cost);
        setMonthlyData(result.data.monthly_cost);setError(null);
      } catch (err) {
        console.error("Error fetching Bedrock cost chart data:", err);
        setError("Failed to load chart data");
      } finally {
        setLoading(false);
      }
    };
    fetchChartData();
  }, []);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ChartComponent
          ref={dailyChartRef}
          title="Daily Bedrock Cost (Last 30 Days)"
          chartData={dailyData}
          isLoading={loading}
          error={error}
          chartType="line"
          formatLabel={formatDayLabel}
        />
        <ChartComponent
          ref={monthlyChartRef}
          title="Monthly Bedrock Cost (Last 6 Months)"
          chartData={monthlyData}
          isLoading={loading}
          error={error}
          chartType="bar"
          formatLabel={formatMonthLabel}
        />
      </div>
    </div>
  );
});

BedrockCostCharts.displayName = "BedrockCostCharts";

export default BedrockCostCharts;